import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { getTenantSites, createTenantSite, deleteTenantSite, bindTenantSiteDomain, bindAdminSiteDomain, unbindTenantSiteDomain, unbindAdminSiteDomain, type TenantSite, type SiteType } from "@/utils/tenantApi";
import { getUsers, type User } from "@/utils/adminApi";
import { getCurrentSiteId, setCurrentSiteId, getMySites, type MySiteInfo } from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { APP_DOMAIN, getSubdomainUrl, getDomainSuffix } from "@/config/domain";
import { CHINA_PROVINCES, getTemplateName, TEMPLATE_NAME_MAP } from "@/constants/provinces";

const statusTabs = [
  { key: "all", label: "全部" },
  { key: "active", label: "运行中" },
  { key: "stopped", label: "已停止" },
  { key: "expired", label: "已到期" },
];

// 站点类型标签
const siteTypeLabels: Record<SiteType | string, { label: string; bg: string; color: string }> = {
  classified: { label: "分类信息", bg: "bg-blue-50", color: "text-blue-600" },
  mall: { label: "多商户商城", bg: "bg-purple-50", color: "text-purple-600" },
  anime: { label: "动漫网站", bg: "bg-pink-50", color: "text-pink-600" },
  music: { label: "音乐网站", bg: "bg-green-50", color: "text-green-600" },
};

// 状态标签
const statusLabels: Record<string, { label: string; bg: string; color: string }> = {
  active: { label: "运行中", bg: "bg-green-50", color: "text-green-600" },
  stopped: { label: "已停止", bg: "bg-gray-50", color: "text-gray-600" },
  expired: { label: "已到期", bg: "bg-red-50", color: "text-red-600" },
};

/**
 * 统一日期格式化：兼容 MySQL datetime、ISO 8601、纯日期格式
 * 同时过滤 "0000-00-00" 等无效日期
 */
function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return "-";
  // 过滤无效日期
  if (/^0{1,4}[-/]0{1,2}[-/]0{1,2}/.test(dateStr)) return "-";
  // 兼容 MySQL datetime 格式: "2025-01-15 10:30:00" → "2025-01-15T10:30:00"
  const normalized = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return "-";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * 计算距离到期天数
 */
function daysUntil(dateStr: string | undefined | null): number {
  if (!dateStr) return 999;
  if (/^0{1,4}[-/]0{1,2}[-/]0{1,2}/.test(dateStr)) return 999;
  const normalized = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return 999;
  const diff = d.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * 判断 expire_at 是否为有效日期（非空、非 null、非无效值）
 */
function hasValidExpire(expireAt: string | undefined | null): boolean {
  if (!expireAt) return false;
  if (/^0{1,4}[-/]0{1,2}[-/]0{1,2}/.test(expireAt)) return false;
  const normalized = expireAt.includes('T') ? expireAt : expireAt.replace(' ', 'T');
  return !isNaN(new Date(normalized).getTime());
}

const PAGE_SIZE = 6;

export default function AdminMySitesPage() {
  const navigate = useNavigate();
  const { isSubdomain, lockedSiteId } = useSite();
  const [sites, setSites] = useState<TenantSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSiteId, setActiveSiteId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [provinceFilter, setProvinceFilter] = useState<string>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const [statusTab, setStatusTab] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [toast, setToast] = useState("");
  const [isTenant, setIsTenant] = useState(false); // 是否是租户角色
  const [isSiteAdmin, setIsSiteAdmin] = useState(false); // 是否是站点管理员
  const [roleReady, setRoleReady] = useState(false); // 角色是否已加载完成

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [editingSite, setEditingSite] = useState<TenantSite | null>(null);
  const [userList, setUserList] = useState<User[]>([]);

  // 获取当前用户角色
  useEffect(() => {
    try {
      const userInfo = localStorage.getItem('user_info');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        setIsTenant(user.role === 'tenant');
        setIsSiteAdmin(user.role === 'site_admin');
      }
    } catch (e) {
      console.error('解析用户信息失败:', e);
    }
    setRoleReady(true);
  }, []);

  // ========= 站点管理员模式：获取 my-sites 数据 =========
  const loadMySites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMySites();
      const rawList: MySiteInfo[] = data.list || [];
      
      // 前端过滤：状态、搜索、省份、模版
      let filtered = rawList;
      if (statusTab !== 'all') {
        filtered = filtered.filter(s => s.status === statusTab);
      }
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        filtered = filtered.filter(s => 
          s.name.toLowerCase().includes(q) || s.subdomain.toLowerCase().includes(q)
        );
      }
      if (provinceFilter !== 'all') {
        filtered = filtered.filter(s => s.province === provinceFilter);
      }
      if (templateFilter !== 'all') {
        filtered = filtered.filter(s => s.template_id === templateFilter);
      }
      
      // 转换为 TenantSite 格式（与后端返回格式完全对齐）
      const converted: TenantSite[] = filtered.map((site) => ({
        id: site.id,
        name: site.name,
        subdomain: site.subdomain,
        status: (site.status as TenantSite['status']) || 'active',
        site_type: (site.site_type as SiteType) || 'classified',
        domains: site.domains || [],
        admin_count: 0,
        owner_name: site.owner_name || '',
        owner_id: site.owner_id,
        created_at: site.created_at || '',
        expire_at: site.expire_at || undefined,
        is_default: site.role === 'owner',
        province: site.province || '',
        template_id: site.template_id || 'classic',
      }));
      
      // 将当前活跃站点排在第一位
      const currentSiteId = getCurrentSiteId();
      if (currentSiteId) {
        converted.sort((a, b) => {
          if (a.id === currentSiteId) return -1;
          if (b.id === currentSiteId) return 1;
          return 0;
        });
      }
      
      setSites(converted);
      setTotal(converted.length);
    } catch (e: any) {
      console.error("加载站点列表失败", e);
      showToast("加载失败: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [statusTab, searchQuery, provinceFilter, templateFilter]);

  // ========= 租户模式：使用和租户代理中心完全相同的 API =========
  const loadTenantSites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTenantSites({
        page: currentPage,
        limit: PAGE_SIZE,
        status: statusTab === 'all' ? '' : statusTab,
        search: searchQuery || '',
        province: provinceFilter !== 'all' ? provinceFilter : undefined,
        template_id: templateFilter !== 'all' ? templateFilter : undefined,
      });
      // 数据格式与租户代理中心完全一致，直接使用
      setSites(data.list || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      console.error("加载站点列表失败", e);
      showToast("加载失败: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusTab, searchQuery, provinceFilter, templateFilter]);

  // 根据角色加载数据
  const loadSites = useCallback(async () => {
    if (!roleReady) return;
    if (isTenant) {
      await loadTenantSites();
    } else {
      await loadMySites();
    }
  }, [roleReady, isTenant, loadTenantSites, loadMySites]);

  // 搜索/状态切换/翻页时自动加载
  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    setActiveSiteId(getCurrentSiteId() || "");
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  // 切换状态标签时重置页码
  const handleStatusChange = (key: string) => {
    setStatusTab(key);
    setCurrentPage(1);
  };

  // 按类型过滤（前端过滤，省份和模版已由 API 或 loadMySites 处理）
  const filteredSites = useMemo(() => {
    if (typeFilter === "all") return sites;
    return sites.filter((s) => s.site_type === typeFilter);
  }, [sites, typeFilter]);

  const displayTotal = useMemo(() => {
    // tenant 模式使用 API 返回的 total，site_admin 模式使用过滤后的数组长度
    return isTenant ? total : filteredSites.length;
  }, [isTenant, total, filteredSites.length]);

  // 分页切片：tenant 模式后端已分页，site_admin 模式需要前端切片
  const paginatedSites = useMemo(() => {
    if (isTenant) return filteredSites;
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSites.slice(start, start + PAGE_SIZE);
  }, [isTenant, filteredSites, currentPage]);

  const totalPages = Math.max(1, Math.ceil(displayTotal / PAGE_SIZE));

  const handleCreateSite = async (site: TenantSite) => {
    setShowCreateModal(false);
    showToast("站点创建成功");
    loadSites(); // 重新加载列表
  };

  const handleSwitchSite = (siteId: string) => {
    setCurrentSiteId(siteId);
    setActiveSiteId(siteId);
    
    // 站点管理员切换站点后跳转到该站点的后台
    if (isSiteAdmin) {
      showToast("正在进入站点后台...");
      navigate("/admin");
    } else {
      showToast("站点切换成功");
    }
  };

  const handleDeleteSite = async (siteId: string) => {
    if (!confirm("确定要删除该站点吗？此操作不可恢复！")) return;
    try {
      await deleteTenantSite(siteId);
      showToast("站点已删除");
      loadSites();
    } catch (e: any) {
      showToast(e.message || "删除失败");
    }
  };

  const handleUpdateDomains = async (siteId: string, newDomain: string) => {
    try {
      // 根据角色选择API：租户用tenant接口，站点管理员用admin接口
      if (isTenant) {
        await bindTenantSiteDomain(siteId, newDomain);
      } else {
        await bindAdminSiteDomain(siteId, newDomain);
      }
      showToast("域名绑定成功");
      setShowDomainModal(false);
      setEditingSite(null);
      loadSites();
    } catch (e: any) {
      showToast(e.message || "绑定失败");
    }
  };

  const handleUnbindDomain = async (siteId: string, domainId: string) => {
    try {
      if (isTenant) {
        await unbindTenantSiteDomain(siteId, domainId);
      } else {
        await unbindAdminSiteDomain(siteId, domainId);
      }
      showToast("域名已解绑");
      loadSites();
    } catch (e: any) {
      showToast(e.message || "解绑失败");
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* 子域名锁定提示 */}
        {isSubdomain && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-2 text-sm text-blue-700">
            <i className="ri-lock-line" />
            <span>当前为子域名访问，已自动锁定站点。如需管理其他站点，请访问</span>
            <a href={`https://${APP_DOMAIN}/admin/my-sites`} className="font-medium text-blue-600 underline">
              主域名后台
            </a>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="搜索站点名称、子域名或域名..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
            >
              <option value="all">全部类型</option>
              <option value="classified">分类信息网站</option>
              <option value="mall">多商户商城</option>
              <option value="anime">动漫网站</option>
              <option value="music">音乐网站</option>
            </select>
            <select
              value={provinceFilter}
              onChange={(e) => { setProvinceFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
            >
              <option value="all">全部省份</option>
              {CHINA_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={templateFilter}
              onChange={(e) => { setTemplateFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
            >
              <option value="all">全部模版</option>
              {Object.entries(TEMPLATE_NAME_MAP).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          {!isSubdomain && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line" />
            </div>
            新建站点
          </button>
          )}
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full mb-6 w-fit">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleStatusChange(tab.key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                statusTab === tab.key
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
              <span className="ml-1.5 text-xs text-gray-400">
                (
                {tab.key === "all"
                  ? sites.length
                  : sites.filter((s) => s.status === tab.key).length}
                )
              </span>
            </button>
          ))}
        </div>

        {/* Sites Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-2 text-gray-500">
              <i className="ri-loader-2-line animate-spin text-xl" />
              <span>加载中...</span>
            </div>
          </div>
        ) : paginatedSites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <div className="w-16 h-16 flex items-center justify-center mb-4">
              <i className="ri-global-line text-3xl" />
            </div>
            <p className="text-sm">暂无站点</p>
            <button onClick={() => setShowCreateModal(true)} className="mt-4 px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors cursor-pointer">
              创建第一个站点
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {paginatedSites.map((site) => {
              const status = statusLabels[site.status] || statusLabels.active;
              const type = siteTypeLabels[site.site_type] || siteTypeLabels.classified;
              const isActive = site.id === activeSiteId;
              const expireDays = daysUntil(site.expire_at || "");
              return (
                <div
                  key={site.id}
                  className={`bg-white rounded-2xl border p-5 transition-all ${
                    isActive ? "border-orange-300 shadow-sm" : "border-gray-100 hover:border-gray-200"
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${isActive ? "bg-orange-50" : "bg-gray-50"}`}>
                        <i className={`ri-global-line ${isActive ? "text-orange-500" : "text-gray-400"}`} />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800">{site.name}</h4>
                        <a
                          href={getSubdomainUrl(site.subdomain)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-orange-500 hover:text-orange-600 hover:underline mt-0.5 inline-flex items-center gap-1"
                        >
                          {site.subdomain}{getDomainSuffix()}
                          <i className="ri-external-link-line text-[10px]" />
                        </a>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Type + Province + Template + Default */}
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${type.bg} ${type.color}`}>
                      {type.label}
                    </span>
                    {site.province && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-teal-50 text-teal-600">
                        <i className="ri-map-pin-line text-[10px]" />
                        {site.province}
                      </span>
                    )}
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-600">
                      {getTemplateName(site.template_id)}
                    </span>
                    {site.is_default && (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-600">
                        默认
                      </span>
                    )}
                  </div>

                  {/* Domains */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-400 mb-1.5">已绑定域名</p>
                    <div className="flex flex-wrap gap-1.5">
                      {site.domains && site.domains.length > 0 ? (
                        site.domains.map((d) => (
                          <a
                            key={d}
                            href={`http://${d}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 hover:bg-orange-50 rounded-md text-xs text-gray-600 hover:text-orange-500 transition-colors"
                          >
                            <i className="ri-link text-xs" />
                            {d}
                            <i className="ri-external-link-line text-[10px]" />
                          </a>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">暂无绑定域名</span>
                      )}
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="space-y-1.5 mb-4 text-xs text-gray-500">
                    <div className="flex items-center justify-between">
                      <span>创建时间</span>
                      <span>{formatDate(site.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>到期时间</span>
                      <span className={expireDays < 30 && expireDays > 0 ? "text-red-500 font-medium" : ""}>
                        {hasValidExpire(site.expire_at) ? formatDate(site.expire_at!) : '永久'}
                        {expireDays < 30 && expireDays > 0 && ` (${expireDays}天后)`}
                      </span>
                    </div>
                    {site.province && (
                      <div className="flex items-center justify-between">
                        <span>所在省份</span>
                        <span className="text-gray-700 font-medium">{site.province}</span>
                      </div>
                    )}
                    {site.owner_name && (
                      <div className="flex items-center justify-between">
                        <span>站点管理员</span>
                        <span className="text-orange-600 font-medium">{site.owner_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                    {isActive ? (
                      <span className="flex-1 py-2 text-center text-xs font-medium text-orange-500 bg-orange-50 rounded-lg">
                        当前站点
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSwitchSite(site.id)}
                        className="flex-1 py-2 text-center text-xs font-medium text-gray-600 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                      >
                        切换至此站点
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingSite(site);
                        setShowDomainModal(true);
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-link" />
                      </div>
                      域名管理
                    </button>
                    <button
                      onClick={() => handleDeleteSite(site.id)}
                      className="px-3 py-2 text-xs font-medium text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="删除站点"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-delete-bin-line" />
                      </div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  p === currentPage
                    ? "bg-orange-500 text-white"
                    : "border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-500"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateSiteModal onClose={() => setShowCreateModal(false)} onCreate={handleCreateSite} isTenant={isTenant} />
        )}

        {/* Domain Modal */}
        {showDomainModal && editingSite && (
          <DomainModal
            site={editingSite}
            onClose={() => { setShowDomainModal(false); setEditingSite(null); }}
            onUpdate={handleUpdateDomains}
            onUnbind={handleUnbindDomain}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-800 text-white text-sm rounded-xl shadow-lg">
            <i className="ri-checkbox-circle-line text-green-400" />
            {toast}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

/* ── Create Site Modal ── */
function CreateSiteModal({ onClose, onCreate, isTenant }: { onClose: () => void; onCreate: (s: TenantSite) => void; isTenant: boolean }) {
  const [name, setName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [siteType, setSiteType] = useState<SiteType>("classified");
  const [province, setProvince] = useState("");
  const [domain, setDomain] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [userList, setUserList] = useState<{id: string; username: string; nickname: string}[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // 非租户用户创建站点时，自动设置为当前用户
  useEffect(() => {
    if (!isTenant) {
      const userInfo = localStorage.getItem('user_info');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        setOwnerId(user.id);
        setOwnerName(user.nickname || user.username);
      }
    }
  }, [isTenant]);

  // 获取用户列表（仅代理商需要选择站点管理员）
  useEffect(() => {
    if (!isTenant) return; // 非租户不需要选择管理员，创建者即管理员

    async function fetchUsers() {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        const siteId = localStorage.getItem('current_site_id') || '';
        const res = await fetch('/api/admin/users?page=1&page_size=100', {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Site-Id': siteId,
          },
        });
        const data = await res.json();
        if (data.code === 0 && data.data?.list) {
          setUserList(data.data.list);
        }
      } catch (e) {
        console.error('获取用户列表失败', e);
      } finally {
        setLoadingUsers(false);
      }
    }
    fetchUsers();
  }, [isTenant]);

  const handleOwnerChange = (userId: string) => {
    setOwnerId(userId);
    const user = userList.find(u => u.id === userId);
    setOwnerName(user ? (user.nickname || user.username) : '');
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError("请输入站点名称"); return; }
    if (!subdomain.trim()) { setError("请输入子域名标识"); return; }
    if (!/^\w{2,20}$/.test(subdomain)) { setError("子域名只能包含字母、数字、下划线，长度2-20位"); return; }
    if (isTenant && !ownerId) { setError("请选择站点管理员"); return; } // 仅租户需要验证

    setSubmitting(true);
    setError("");

    try {
      const result = await createTenantSite({
        name: name.trim(),
        subdomain: subdomain.trim().toLowerCase(),
        site_type: siteType,
        province: province || undefined,
        admin_user_id: ownerId || undefined,
      });

      const newSite: TenantSite = {
        id: result.id,
        name: name.trim(),
        subdomain: result.subdomain || subdomain.trim().toLowerCase(),
        domains: domain.trim() ? [domain.trim().toLowerCase()] : [],
        status: "active",
        site_type: siteType,
        province: province || '',
        template_id: 'classic',
        created_at: new Date().toISOString(),
        expire_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        owner_id: ownerId,
        owner_name: ownerName,
      };
      onCreate(newSite);
    } catch (e: any) {
      setError(e.message || '创建失败，请重试');
      setSubmitting(false);
      return;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">新建站点</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点名称</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如：我的分类站"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">子域名标识</label>
            <div className="flex items-center">
              <input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                placeholder="如：mysite"
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-l-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
              />
              <span className="px-4 py-2.5 bg-gray-100 border border-l-0 border-gray-200 rounded-r-xl text-sm text-gray-500 whitespace-nowrap">
                {getDomainSuffix()}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点类型</label>
            <div className="grid grid-cols-2 gap-2">
              {(["classified", "mall", "anime", "music"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setSiteType(t)}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-xl border transition-all cursor-pointer ${
                    siteType === t
                      ? "border-orange-300 bg-orange-50 text-orange-600"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    {siteType === t ? (
                      <i className="ri-checkbox-circle-fill text-orange-500" />
                    ) : (
                      <i className="ri-checkbox-blank-circle-line text-gray-300" />
                    )}
                  </div>
                  {siteTypeLabels[t].label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">所在省份</label>
            <select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
            >
              <option value="">请选择省份（可选）</option>
              {CHINA_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          {isTenant && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                站点管理员 <span className="text-red-500">*</span>
              </label>
              <select
                value={ownerId}
                onChange={(e) => handleOwnerChange(e.target.value)}
                disabled={loadingUsers}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
              >
                <option value="">请选择站点管理员</option>
                {userList.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.nickname || user.username}
                    {user.phone && ` (${user.phone})`}
                  </option>
                ))}
              </select>
              {loadingUsers && <p className="text-xs text-gray-400 mt-1">加载用户列表中...</p>}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              自定义域名 <span className="text-gray-400 font-normal">（可选）</span>
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="如：www.example.com"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <p className="text-xs text-gray-400 mt-1">创建后也可随时绑定更多域名</p>
          </div>
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <i className="ri-loader-2-line animate-spin" />
                创建中...
              </>
            ) : (
              '创建站点'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Domain Modal ── */
function DomainModal({
  site,
  onClose,
  onUpdate,
  onUnbind,
}: {
  site: TenantSite;
  onClose: () => void;
  onUpdate: (siteId: string, domain: string) => void;
  onUnbind?: (siteId: string, domainId: string) => void;
}) {
  const [domain, setDomain] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [wwwPreview, setWwwPreview] = useState("");

  // 为域名构建ID（用于解绑时传参）
  const [domains, setDomains] = useState<{id: string; domain: string}[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setDomains(site.domains.map((d, i) => ({ id: `d${i}`, domain: d })));
    setLoading(false);
  }, [site]);

  // 实时预览自动www域名
  const updateWwwPreview = (val: string) => {
    const trimmed = val.trim().toLowerCase();
    if (!trimmed || !/^[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+$/.test(trimmed)) {
      setWwwPreview("");
      return;
    }
    const parts = trimmed.split('.');
    if (parts.length >= 2 && parts[0] !== 'www') {
      const wwwDomain = 'www.' + trimmed;
      if (!site.domains?.includes(wwwDomain)) {
        setWwwPreview(wwwDomain);
        return;
      }
    }
    if (parts.length >= 3 && parts[0] === 'www') {
      const rootDomain = parts.slice(1).join('.');
      if (!site.domains?.includes(rootDomain)) {
        setWwwPreview(rootDomain);
        return;
      }
    }
    setWwwPreview("");
  };

  const handleSubmit = async () => {
    if (!domain.trim()) {
      setError("请输入域名");
      return;
    }
    if (!/^[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+$/.test(domain.trim())) {
      setError("域名格式无效");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onUpdate(site.id, domain.trim().toLowerCase());
    } catch (e: any) {
      setError(e.message || "绑定失败");
      setSubmitting(false);
    }
  };

  const handleUnbind = async (domainId: string, domainName: string) => {
    if (!confirm(`确定要解绑域名 "${domainName}" 吗？`)) return;
    try {
      await onUnbind?.(site.id, domainName);
    } catch (err: any) {
      alert(err.message || "解绑失败");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">域名管理 - {site.name}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-information-line text-blue-500" />
            <span className="text-sm font-medium text-blue-700">域名解析说明</span>
          </div>
          <p className="text-xs text-blue-600 leading-relaxed">
            请将您的域名 CNAME 记录指向「{site.subdomain}{getDomainSuffix()}」，添加记录后下方输入域名完成绑定。
          </p>
        </div>

        {/* 已绑定域名列表 */}
        {!loading && domains.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">已绑定域名</label>
            <div className="space-y-2">
              {domains.map((d) => (
                <div key={d.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                  <a
                    href={`http://${d.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-700 hover:text-orange-500"
                  >
                    <i className="ri-link text-gray-400" />
                    {d.domain}
                    <i className="ri-external-link-line text-[10px] text-gray-400" />
                  </a>
                  <button
                    onClick={() => handleUnbind(d.id, d.domain)}
                    className="text-xs text-red-500 hover:text-red-600 cursor-pointer p-1"
                    title="解绑域名"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 自动www域名预览 */}
        {wwwPreview && (
          <div className="p-3 bg-green-50 rounded-xl border border-green-200 mb-4 flex items-center gap-2">
            <i className="ri-check-double-line text-green-500" />
            <span className="text-sm text-green-700">
              将同时绑定 <b className="font-mono">{wwwPreview}</b>
            </span>
          </div>
        )}

        <div className="mb-4">
          <input
            type="text"
            value={domain}
            onChange={(e) => { setDomain(e.target.value); updateWwwPreview(e.target.value); }}
            placeholder="如：www.example.com"
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          <p className="text-xs text-gray-400 mt-2">
            已绑定域名：{site.domains?.length ? site.domains.join(", ") : "暂无"}
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2 mb-4">
            <i className="ri-error-warning-line" />
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <i className="ri-loader-2-line animate-spin" />
                绑定中...
              </>
            ) : (
              "绑定域名"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}